import React, { useState, useEffect } from "react";
import TablerLayout from "../components/layout/TablerLayout";
import StepperNavButton from "../components/stepper/StepperNavButton";
import { useStepNavigate } from "../hooks/useStepNavigate";
import { useOrder } from "../context/OrderContext";
import { dataService } from "../services/dataService";
import { IconPlus } from '@tabler/icons-react';
import { useOrderSave } from "../context/OrderSaveContext";
import { showErrorToast } from "../components/toasts/ToastService";

const ProjektValaszto = () => {
    const { order, updateOrder } = useOrder();
    const [projects, setProjects] = useState([]);
    const [originalProjects, setOriginalProjects] = useState([]); 
    const [showCodeInput, setShowCodeInput] = useState(false);
    const [selectedCode, setSelectedCode] = useState(null);
    const [newCode, setNewCode] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useStepNavigate();
    const {saveOrder} = useOrderSave();

    useEffect(() => {
        const existingCode = order.projekt?.hmgenproject_collection_code;
        if ((existingCode && existingCode !== "")) {
            setShowCodeInput(true);
            setSelectedCode(existingCode);
        }
    }, [order.projekt]);

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                setLoading(true);
                const response = await dataService.getAvailableProjects();
                
                if (response.data?.status === 'ok') {
                    const projectData = response.data.data || [];
                    let projectCodes = projectData.map(p => p.project_collection_code);
                    
                    const existingCode = order.projekt?.hmgenproject_collection_code;
                    
                    projectCodes.sort();
                    
                    setOriginalProjects(projectCodes.map(code => ({ code })));
                    
                    if (existingCode && !projectCodes.includes(existingCode)) {
                        setProjects([
                            { code: existingCode, isCustom: true },
                            ...projectCodes.map(code => ({ code }))
                        ]);
                    } else {
                        setProjects(projectCodes.map(code => ({ code })));
                    }
                } else {
                    throw new Error(response.data?.message || 'Sikertelen lekérdezés');
                }
            } catch (err) {
                console.error("Hiba a projektek lekérésekor:", err);
                setError(err.message || 'Ismeretlen hiba történt');
            } finally {
                setLoading(false);
            }
        };
        
        fetchProjects();
    }, [order.projekt]);

    const handleSelectCode = (code, szam = null) => {
        setSelectedCode(code);
    };

    const handleAddNewCode = () => {
        if (!newCode.trim()) return;

        const upperCaseCode = newCode.toUpperCase();
        const numericSzam = parseInt(newCode, 10);

        if (originalProjects.some(p => p.code === upperCaseCode || p.szam === numericSzam)) {
            setSelectedCode(upperCaseCode);
            return;
        }

        setProjects([
            { code: upperCaseCode, szam: numericSzam, isCustom: true },
            ...originalProjects
        ]);

        setSelectedCode(upperCaseCode);
    };
    
    const handleInputChange = (e) => {
        setNewCode(e.target.value.toUpperCase());
    };

    const handleSetUseProject = (useProject) => {
        setShowCodeInput(useProject);
        if (!useProject) {
            setSelectedCode(null);

            updateOrder(prev => ({
                ...prev,
                projekt: {
                    ...prev.projekt,
                    hmgenproject_collection_code: null
                }
            }));
        }
    };

    
    const handleNext = async () => {
        const order_type = order.hmeliorder?.hmeliorder_order_type;
        const depot_prefix = order.hmeliorder?.hmeliorder_depot_prefix;

        try {
            setLoading(true);

            const updatedOrder = {
                ...order,
                projekt: {
                    ...order.projekt,
                    hmgenproject_collection_code: showCodeInput ? selectedCode || null : null,
                },
            };

            updateOrder(updatedOrder);

            const result = await saveOrder(updatedOrder);
            if (result.success) {
                if (order_type === 1) {
                    navigate('/VenyesTipus');
                } else if (depot_prefix === "BETÉT") {
                    navigate('/BetetTartozek');
                } else {
                    navigate('/Kerdesek');
                }
            } else {
                showErrorToast("Hiba történt a rendelés mentésekor!");
            }
        } catch (error) {
            console.error("Hiba a rendelés mentésekor:", error);
            showErrorToast("Hiba történt a rendelés mentésekor!");
        } finally {
            setLoading(false);
        }
    };
    
    const canProceed = !showCodeInput || selectedCode;

    return (
        <TablerLayout title="Projekt gyűjtőkód beállítása">
            <div className="row justify-content-center">
                <div className="col-12 col-md-10 justify-content-center">
                    <div className="d-flex flex-column flex-md-row gap-3 mb-4 justify-content-center">
                        <button type="button"
                                className={`btn w-100 py-4 px-3 d-flex align-items-center fw-bold ${!showCodeInput ? 'btn-primary' : 'btn-outline-primary'}`}
                                style={{ fontSize: 22 }}
                                onClick={() => handleSetUseProject(false)}
                        >
                            Nincs projekt gyűjtőkód
                        </button>
                        <button type="button"
                                className={`btn w-100 py-4 px-3 d-flex align-items-center fw-bold ${showCodeInput ? 'btn-primary' : 'btn-outline-primary'}`}
                                style={{ fontSize: 22 }}
                                onClick={() => handleSetUseProject(true)}
                        >
                            Megadom a gyűjtőkódot
                        </button>
                    </div>
                    
                    {showCodeInput && (
                        <div className="project-selection mt-4">
                            <div className="mb-4">
                                <div className="input-group mb-3">
                                    <input
                                        type="text"
                                        className="form-control form-control-lg"
                                        placeholder="Új gyűjtőkód megadása"
                                        value={newCode}
                                        onChange={handleInputChange}
                                        style={{textTransform: 'uppercase'}}
                                    />
                                    <button
                                        className="btn btn-primary btn-lg"
                                        onClick={handleAddNewCode}
                                        disabled={!newCode.trim()}
                                    >
                                        <IconPlus size={20} /> Hozzáadás
                                    </button>
                                </div>
                            </div>

                            {loading && (
                                <div className="d-flex justify-content-center my-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Betöltés...</span>
                                    </div>
                                </div>
                            )}
                            
                            {error && (
                                <div className="alert alert-danger" role="alert">
                                    <h4 className="alert-heading">Hiba történt!</h4>
                                    <p>{error}</p>
                                </div>
                            )}
                            
                            {!loading && !error && (
                                <div className="project-list">
                                    <h5 className="mb-3">Válasszon a meglévő gyűjtőkódok közül:</h5>
                                    
                                    {projects.length > 0 ? (
                                        <div className="row g-3 mb-3">
                                            {projects.map((project, index) => (
                                                <div className="col-12 col-sm-6 col-md-4" key={project.code + (project.isCustom ? '-custom' : '')}>
                                                    <button
                                                        onClick={() => handleSelectCode(project.code, project.szam)}
                                                        className={`btn text-center fw-bold ${
                                                            selectedCode === project.code ? 'btn-primary' : 'btn-outline-primary'
                                                            
                                                        } w-100 text-break `}
                                                    >
                                                        {project.code} {project.szam && `(${project.szam})`}
                                                        {project.isCustom && (
                                                            <span className="badge bg-success ms-2 p-0">Új</span>
                                                        )}
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="alert alert-info">
                                            Nincsenek elérhető projekt gyűjtőkódok. Kérjük, adjon meg egy újat!
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    
                    <div className="d-flex justify-content-between mt-5">
                        <StepperNavButton
                            side="right"
                            label="Tovább"
                            onClick={handleNext}
                            disabled={!canProceed}
                        />
                    </div>
                </div>
            </div>
        </TablerLayout>
    );
};

export default ProjektValaszto;